// The table's right-click menu: where it opens, on what kind of target (a
// file row, a folder row, or empty background), and which items it offers —
// gated by capabilities (delete) and wired to the name-editing actions.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IDocument, IFolder } from '@apis/documents';
import { canAnnotate } from '@components/Pages/AnnotationsPage/annotations.url';

interface ICtxMenu {
  x: number;
  y: number;
  targetId: string;
  kind: 'doc' | 'folder' | 'background';
}

interface ICtxMenuItem {
  label: string;
  onClick: () => void;
  danger?: boolean;
}

interface IUseTableContextMenuArgs {
  documents: IDocument[];
  folders: IFolder[];
  canDelete: boolean;
  onUploadNewVersion?: (id: string) => void;
  /** Opens the annotations page on the row's presented version. */
  onMarkupDoc?: (id: string) => void;
  onDeleteDoc: (id: string) => void;
  onDeleteFolder?: (id: string) => void;
  /** Present only where creating a folder here is allowed. */
  onNewFolder?: () => void;
  /** Present only where renaming is allowed — likewise each optional action. */
  startRename?: (fileId: string) => void;
  startRenameFolder?: (folderId: string) => void;
}

export function useTableContextMenu({
  documents, folders, canDelete, onUploadNewVersion, onMarkupDoc, onDeleteDoc, onDeleteFolder,
  onNewFolder, startRename, startRenameFolder,
}: IUseTableContextMenuArgs) {
  const { t } = useTranslation();
  const [ctxMenu, setCtxMenu] = useState<ICtxMenu | null>(null);

  function openRowMenu(e: React.MouseEvent, fileId: string) {
    e.preventDefault();
    e.stopPropagation();
    setCtxMenu({ x: e.clientX, y: e.clientY, targetId: fileId, kind: 'doc' });
  }

  // System roots have no row actions (like the sidebar, where their only item
  // is "new subfolder" — which wouldn't be visible in this view anyway).
  function openFolderMenu(e: React.MouseEvent, folderId: string) {
    e.preventDefault();
    e.stopPropagation();
    if (folders.find((f) => f.id === folderId)?.isSystem) { setCtxMenu(null); return; }
    setCtxMenu({ x: e.clientX, y: e.clientY, targetId: folderId, kind: 'folder' });
  }

  // Right-click on empty table space: "New folder" in the current folder —
  // mirrors the sidebar. Suppressed (like the sidebar background) where
  // creation isn't allowed: the virtual root and depth-limited folders.
  function openBackgroundMenu(e: React.MouseEvent) {
    e.preventDefault();
    if (!onNewFolder) { setCtxMenu(null); return; }
    setCtxMenu({ x: e.clientX, y: e.clientY, targetId: '', kind: 'background' });
  }

  // The annotations item opens the row's presented version — same gate as the
  // drawer's document-level Markup button, including for a file the version
  // index doesn't govern (its single version is synthesised).
  const ctxDoc = ctxMenu?.kind === 'doc' ? documents.find((d) => d.id === ctxMenu.targetId) : undefined;
  const canMarkupCtx = !!onMarkupDoc && !!ctxDoc && canAnnotate(ctxDoc.name, ctxDoc.id);

  const ctxItems: ICtxMenuItem[] = ctxMenu
    ? ctxMenu.kind === 'doc'
      ? [
          ...(startRename ? [
            { label: t('documents.contextMenu.rename'), onClick: () => startRename(ctxMenu.targetId) },
          ] : []),
          ...(onUploadNewVersion ? [
            { label: t('documents.contextMenu.uploadNewVersion'), onClick: () => onUploadNewVersion(ctxMenu.targetId) },
          ] : []),
          ...(canMarkupCtx ? [
            { label: t('documents.annotations.action'), onClick: () => onMarkupDoc?.(ctxMenu.targetId) },
          ] : []),
          ...(canDelete ? [
            { label: t('documents.contextMenu.delete'), onClick: () => onDeleteDoc(ctxMenu.targetId), danger: true },
          ] : []),
        ]
      : ctxMenu.kind === 'folder'
        ? [
            ...(startRenameFolder ? [
              { label: t('documents.contextMenu.rename'), onClick: () => startRenameFolder(ctxMenu.targetId) },
            ] : []),
            ...(canDelete ? [
              { label: t('documents.contextMenu.delete'), onClick: () => onDeleteFolder?.(ctxMenu.targetId), danger: true },
            ] : []),
          ]
        : [
            { label: t('documents.contextMenu.newFolder'), onClick: () => onNewFolder?.() },
          ]
    : [];

  return { ctxMenu, closeCtxMenu: () => setCtxMenu(null), openRowMenu, openFolderMenu, openBackgroundMenu, ctxItems };
}